/**
 * BLRRD Tools — Gradient (Phase 4.E.tools.1, Wave 12.1).
 *
 * Linear + radial gradient fill across a Float32 ACEScg-linear RGBA
 * buffer. Stops are interpolated linearly in scene-linear ACEScg —
 * the right choice because the renderer always converts back to
 * display space, and interpolating in display sRGB would bake in
 * the gamma curve and produce the dark-middle artefacts that LinkedIn
 * UX cards are famous for.
 *
 * Algorithm (Hughes, Computer Graphics: Principles and Practice 3rd
 * ed., §10.4 "Procedural Texture"):
 *
 *   Linear:  t(x, y) = ((P - A) · (B - A)) / |B - A|²
 *   Radial:  t(x, y) = |P - A| / |B - A|
 *
 * Where A, B are the user-defined start/end points and P is the pixel
 * centre. Result `t` is clamped to [0, 1] (no extend mode v1 — we'll
 * add "repeat" / "mirror" / "pad" later).
 *
 * Stop interpolation: piecewise linear between adjacent stops sorted
 * by `t`. Endpoints clamp (t < stops[0].t → stops[0].color).
 *
 * License: MIT clean-room. Standard graphics math from Hughes §10.4.
 * No GIMP source consulted.
 */

/* ── Stop evaluation ─────────────────────────────────────────────── */

/**
 * Sort + validate the stop list. Returns a NEW sorted array, leaving
 * caller's gradient definition untouched. Defensive copy because the
 * UI may edit stops while a render is in flight.
 */
function normalizeStops(stops) {
    if (!Array.isArray(stops) || stops.length === 0) {
        throw new Error('gradient: at least one stop required');
    }
    const out = stops.map(s => {
        const t = Number(s.t);
        if (!Number.isFinite(t)) {
            throw new Error('gradient: stop.t must be finite');
        }
        if (!Array.isArray(s.color) && !ArrayBuffer.isView(s.color)) {
            throw new Error('gradient: stop.color must be an array');
        }
        const c = [
            Number(s.color[0]) || 0,
            Number(s.color[1]) || 0,
            Number(s.color[2]) || 0,
            s.color.length >= 4 ? Number(s.color[3]) : 1,
        ];
        return { t: clamp01(t), color: c };
    });
    out.sort((a, b) => a.t - b.t);
    return out;
}

/**
 * Evaluate the gradient at parameter t ∈ [0, 1]. Returns [r, g, b, a].
 *
 * @param {{ stops: Array<{ t: number, color: number[] }>, type?: string }} gradient
 * @param {number} t
 */
export function evalGradient(gradient, t) {
    const stops = gradient.__normalizedStops || normalizeStops(gradient.stops);
    const u = clamp01(t);

    /* Endpoint clamps. */
    if (u <= stops[0].t) return stops[0].color.slice();
    if (u >= stops[stops.length - 1].t) return stops[stops.length - 1].color.slice();

    /* Bisect — typical stop counts are 2..5 so linear scan is fine. */
    for (let i = 0; i < stops.length - 1; i++) {
        const a = stops[i];
        const b = stops[i + 1];
        if (u >= a.t && u <= b.t) {
            const span = b.t - a.t;
            const f = span <= 1e-9 ? 0 : (u - a.t) / span;
            return [
                a.color[0] + (b.color[0] - a.color[0]) * f,
                a.color[1] + (b.color[1] - a.color[1]) * f,
                a.color[2] + (b.color[2] - a.color[2]) * f,
                a.color[3] + (b.color[3] - a.color[3]) * f,
            ];
        }
    }
    /* Should be unreachable given clamp + sort, but defensive. */
    return stops[stops.length - 1].color.slice();
}

/* ── Linear-gradient parameter ───────────────────────────────────── */

/* The parameter `t` at a pixel (px, py) for the line A→B is the scalar
 * projection of (P - A) onto (B - A), normalized by |B - A|². */
function linearT(px, py, ax, ay, bx, by, invLenSq) {
    const dx = bx - ax;
    const dy = by - ay;
    const ex = px - ax;
    const ey = py - ay;
    return (ex * dx + ey * dy) * invLenSq;
}

/* ── Radial-gradient parameter ───────────────────────────────────── */

function radialT(px, py, cx, cy, invRadius) {
    const dx = px - cx;
    const dy = py - cy;
    return Math.sqrt(dx * dx + dy * dy) * invRadius;
}

/* ── Mask helper (shared semantics with bucket-fill) ─────────────── */

function maskWeight(mask, i) {
    if (!mask) return 1;
    const v = mask[i];
    return v < 0 ? 0 : v > 1 ? 1 : v;
}

/* ── Blend (matches bucket-fill normal alpha-composite) ──────────── */

function blendPixel(rgba, j, r, g, b, a, alpha) {
    if (alpha <= 0) return;
    if (alpha >= 1) {
        rgba[j]     = r;
        rgba[j + 1] = g;
        rgba[j + 2] = b;
        rgba[j + 3] = a;
        return;
    }
    const inv = 1 - alpha;
    rgba[j]     = rgba[j]     * inv + r * alpha;
    rgba[j + 1] = rgba[j + 1] * inv + g * alpha;
    rgba[j + 2] = rgba[j + 2] * inv + b * alpha;
    rgba[j + 3] = rgba[j + 3] * inv + a * alpha;
}

/* ── applyGradient ──────────────────────────────────────────────── */

/**
 * Apply a gradient to the buffer in-place.
 *
 * @param {Float32Array} rgba   length w*h*4, ACEScg-linear
 * @param {number}       w
 * @param {number}       h
 * @param {{
 *   type: 'linear' | 'radial',
 *   stops: Array<{ t: number, color: number[] }>,
 *   start: { x: number, y: number },
 *   end:   { x: number, y: number },
 * }} gradient
 * @param {{
 *   selectionMask?: Float32Array,
 *   opacity?: number,
 *   blendMode?: 'normal' | 'replace',
 * }} [opts]
 * @returns {number} count of pixels written (= w*h when no mask)
 */
export function applyGradient(rgba, w, h, gradient, opts = {}) {
    if (!rgba || w <= 0 || h <= 0) {
        throw new Error('applyGradient: invalid buffer dimensions');
    }
    if (rgba.length < w * h * 4) {
        throw new Error('applyGradient: buffer too short for w*h*4');
    }
    if (!gradient || !gradient.type || !gradient.start || !gradient.end) {
        throw new Error('applyGradient: gradient.{type,start,end} required');
    }

    const stops      = normalizeStops(gradient.stops);
    /* Cache the sorted stop list on a synthetic key so evalGradient
     * doesn't re-normalize per pixel. We don't mutate `gradient`. */
    const cached     = { type: gradient.type, __normalizedStops: stops };

    const ax = gradient.start.x;
    const ay = gradient.start.y;
    const bx = gradient.end.x;
    const by = gradient.end.y;

    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-9) {
        /* Degenerate gradient — start == end. Fill the masked region
         * with the last stop's colour. */
        const c = stops[stops.length - 1].color;
        return fillSolid(rgba, w, h, c, opts);
    }
    const invLenSq = 1 / lenSq;
    const len = Math.sqrt(lenSq);
    const invRadius = 1 / len;

    const mask    = opts.selectionMask || null;
    const opacity = clamp01(opts.opacity ?? 1);

    if (mask && mask.length < w * h) {
        throw new Error('applyGradient: selectionMask too short for w*h');
    }

    const isRadial = gradient.type === 'radial';
    let count = 0;

    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = y * w + x;
            const mw = maskWeight(mask, i);
            if (mw <= 0) continue;

            const t = isRadial
                ? radialT(x + 0.5, y + 0.5, ax, ay, invRadius)
                : linearT(x + 0.5, y + 0.5, ax, ay, bx, by, invLenSq);
            const c = evalGradient(cached, t);

            const j = i * 4;
            blendPixel(rgba, j, c[0], c[1], c[2], c[3], opacity * mw * c[3]);
            count++;
        }
    }
    return count;
}

/* Edge case helper: degenerate (start==end) gradient. */
function fillSolid(rgba, w, h, color, opts) {
    const mask    = opts.selectionMask || null;
    const opacity = clamp01(opts.opacity ?? 1);
    const r = color[0], g = color[1], b = color[2];
    const a = color.length >= 4 ? color[3] : 1;
    let count = 0;
    for (let i = 0; i < w * h; i++) {
        const mw = maskWeight(mask, i);
        if (mw <= 0) continue;
        const j = i * 4;
        blendPixel(rgba, j, r, g, b, a, opacity * mw * a);
        count++;
    }
    return count;
}

/* ── Stop helpers (exported for UI use) ──────────────────────────── */

/**
 * Insert a new stop at `t`, with the colour interpolated from the
 * current stops at that t (so adding a stop is initially a no-op
 * visually). Returns a fresh stops array (immutable update).
 */
export function insertStop(stops, t) {
    const normalized = normalizeStops(stops);
    const c = evalGradient({ __normalizedStops: normalized }, t);
    const next = normalized.slice();
    next.push({ t: clamp01(t), color: c });
    next.sort((a, b) => a.t - b.t);
    return next;
}

/**
 * Remove the stop at index `i`. Returns a fresh stops array.
 */
export function removeStop(stops, i) {
    if (stops.length <= 2) return stops.slice(); /* keep at least 2 */
    if (i < 0 || i >= stops.length) return stops.slice();
    return stops.slice(0, i).concat(stops.slice(i + 1));
}

/**
 * Default 2-stop white→black gradient. Useful seed for the UI.
 */
export const DEFAULT_GRADIENT = Object.freeze({
    type: 'linear',
    stops: [
        { t: 0, color: [1, 1, 1, 1] },
        { t: 1, color: [0, 0, 0, 1] },
    ],
    start: { x: 0, y: 0 },
    end:   { x: 1, y: 0 },
});

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
